// FILE: frontend/src/components/TasteMatchCard.js  (NEW FILE)
// Used on: Discover People page, Profile page, Movie Detail page
import React from 'react';
import { Link } from 'react-router-dom';
import { UserPlus, UserCheck, Flame, Film } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function TasteMatchCard({ user, currentUser, onFollowChange }) {
  const [isFollowing, setIsFollowing] = React.useState(false);
  const [loading, setLoading]         = React.useState(false);

  const sharedCount  = user.shared_count || 0;
  const sharedMovies = user.shared_movies || [];

  // Score colour: green > 10, amber 5–10, gray < 5
  const scoreColor =
    sharedCount >= 10 ? 'text-green-400'  :
    sharedCount >= 5  ? 'text-yellow-400' : 'text-muted-foreground';

  const handleFollow = async (e) => {
    e.preventDefault();
    if (!currentUser) { toast.error('Login to follow'); return; }
    setLoading(true);
    try {
      if (isFollowing) {
        await axios.delete(`${BACKEND_URL}/api/follow/${user.user_id}`, { withCredentials: true });
        setIsFollowing(false);
        toast.success('Unfollowed');
      } else {
        await axios.post(
          `${BACKEND_URL}/api/follow`,
          { target_user_id: user.user_id },
          { withCredentials: true }
        );
        setIsFollowing(true);
        toast.success(`Following ${user.name}!`);
      }
      onFollowChange?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Something went wrong');
    } finally { setLoading(false); }
  };

  return (
    <div className="bg-card border border-white/6 rounded-2xl p-5 hover:border-white/12 transition-colors group">
      {/* User header */}
      <Link to={`/profile/${user.user_id}`} className="flex items-center gap-3 mb-4">
        <img
          src={user.picture}
          alt={user.name}
          className="w-12 h-12 rounded-full border border-white/10 group-hover:border-primary/40 transition-colors"
        />
        <div className="flex-1 min-w-0">
          <p className="font-bold text-white text-sm group-hover:text-primary transition-colors line-clamp-1">
            {user.name}
          </p>
          <p className="text-xs text-muted-foreground">
            {user.follower_count > 0 ? `${user.follower_count} followers` : 'New member'}
          </p>
        </div>

        {/* Match score badge */}
        {sharedCount > 0 && (
          <div className={`flex items-center gap-1 text-xs font-bold ${scoreColor} bg-white/5 border border-white/8 px-2.5 py-1 rounded-full flex-shrink-0`}>
            <Flame className="w-3 h-3" />
            {sharedCount}
          </div>
        )}
      </Link>

      {/* Shared taste label */}
      {sharedCount > 0 && (
        <p className="text-xs text-muted-foreground mb-3 leading-relaxed">
          You and <span className="text-white font-semibold">{user.name}</span> both{' '}
          {sharedCount === 1
            ? 'have 1 movie in common'
            : `share ${sharedCount} movies`}
        </p>
      )}

      {/* Shared movie poster strip */}
      {sharedMovies.length > 0 && (
        <div className="flex gap-1.5 mb-4">
          {sharedMovies.slice(0, 5).map((m, i) => (
            m.poster_path ? (
              <img
                key={m.tmdb_id || i}
                src={`https://image.tmdb.org/t/p/w92${m.poster_path}`}
                alt={m.title}
                title={m.title}
                className="w-10 h-[60px] rounded-lg object-cover border border-white/10 flex-shrink-0"
              />
            ) : (
              <div
                key={m.tmdb_id || i}
                className="w-10 h-[60px] rounded-lg bg-muted flex items-center justify-center flex-shrink-0 border border-white/10"
              >
                <Film className="w-3 h-3 text-muted-foreground" />
              </div>
            )
          ))}
          {sharedCount > 5 && (
            <div className="w-10 h-[60px] rounded-lg bg-white/5 border border-white/8 flex items-center justify-center flex-shrink-0">
              <span className="text-[10px] font-bold text-muted-foreground">+{sharedCount - 5}</span>
            </div>
          )}
        </div>
      )}

      {/* Follow button */}
      {currentUser && currentUser.user_id !== user.user_id && (
        <button
          onClick={handleFollow}
          disabled={loading}
          className={`w-full flex items-center justify-center gap-2 py-2 rounded-xl text-sm font-bold transition-all active:scale-95 disabled:opacity-60 ${
            isFollowing
              ? 'bg-white/8 text-white border border-white/15 hover:bg-red-500/15 hover:text-red-300 hover:border-red-500/30'
              : 'bg-primary text-primary-foreground hover:bg-primary/90 shadow-md shadow-primary/20'
          }`}
        >
          {isFollowing
            ? <><UserCheck className="w-4 h-4" /> Following</>
            : <><UserPlus className="w-4 h-4" /> Follow</>
          }
        </button>
      )}
    </div>
  );
}
